import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SimpleMDE from 'react-simplemde-editor';
import ReactMarkdown from 'react-markdown';
import 'easymde/dist/easymde.min.css';
import remarkGfm from 'remark-gfm';
import type EasyMDE from 'easymde';
import { formatTimestampToLocaleString } from '../utils/time';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import { useToasterStore } from '../stores/ui/toasterStore';

interface ResearchReportEditorProps {
  tokenAddress: string;
  contents: string;
  isEditing: boolean;
  contentsReferencedFrom?: Date;
  contentsReferencedTo?: Date;
  projectSiteUrl?: string;
  communityId?: string;
  onStartEdit: () => void;
  onSave: (nextContents: string) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const ResearchReportEditor: React.FC<ResearchReportEditorProps> = React.memo(({
  tokenAddress,
  contents,
  isEditing,
  contentsReferencedFrom = null,
  contentsReferencedTo = null,
  projectSiteUrl = null,
  communityId = null,
  onStartEdit,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const { showToaster } = useToasterStore();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localEditedContents, setLocalEditedContents] = useState<string>(contents);

  useEffect(() => {
    if (!isEditing) {
      setLocalEditedContents(contents);
    }
  }, [contents, isEditing]);

  const handleRefresh = useCallback(async () => {
    if (!tokenAddress || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await feManagementSystemApi.updateTokenContents(tokenAddress);
      showToaster('Refresh request sent. Data will update shortly.', 'success');
    } catch (error) {
      console.error('최신 정보 갱신 실패:', error);
      showToaster('Failed to refresh data.', 'error');
    } finally {
      setIsRefreshing(false);
    }
  }, [tokenAddress, isRefreshing, showToaster]);

  const handleSave = () => onSave(localEditedContents);
  const editorOptions = useMemo<EasyMDE.Options>(() => ({
    placeholder: '마크다운을 입력하세요...',
    spellChecker: false,
    toolbar: [
      'bold', 'italic', 'heading', '|',
      'quote', 'unordered-list', 'ordered-list', '|',
      'link', 'image', '|',
      'preview', 'side-by-side', 'fullscreen', '|',
      'guide'
    ] as const,
    status: false,
    minHeight: '870px',
    renderingConfig: {
      singleLineBreaks: false,
      codeSyntaxHighlighting: true,
    },
  }), []);
  const formatDate = (date?: Date): string => {
    if (!date) return '';
    return formatTimestampToLocaleString(date, 'en-US', {
      timeZone: 'Asia/Seoul',
      second: '2-digit',
    });
  };
  return (
    <div
      className="flex-[2] bg-gray-800 rounded-lg border border-gray-700 overflow-auto flex flex-col"
      style={{ maxHeight: 'calc(100vh - 300px)' }}
    >
      {/* 헤더 영역 */}
      <div className="px-4 py-3 border-b border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[18px] font-semibold text-white">Research Report</h3>
          {!isEditing ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 transition-colors disabled:opacity-50"
                title="Update with latest data"
              >
                {isRefreshing ? (
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg 
                    className="mr-1.5"
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <path d="M23 4v6h-6"></path>
                    <path d="M1 20v-6h6"></path>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                  </svg>
                )}
               Update with latest data
              </button>
              <button
                onClick={onStartEdit}
                className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 transition-colors"
                title="편집"
              >
                <svg 
                  width="14" 
                  height="14" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
            </div>
          ) : (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : '저장'}
            </button>
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="px-3 py-1.5 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              취소
            </button>
          </div>
          )}
        </div>
        {/* 메타 정보 */}
            {(contentsReferencedFrom || contentsReferencedTo || projectSiteUrl || communityId) && (
          <div className="flex flex-wrap items-center gap-4 text-[14px] text-gray-400 mt-2">
            {contentsReferencedFrom && contentsReferencedTo && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Reference period:</span>
                <span className="text-gray-300">
                  {formatDate(contentsReferencedFrom)} ~ {formatDate(contentsReferencedTo)}
                </span>
              </div>
            )}
            {projectSiteUrl && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Project URL:</span>
                <a
                  href={projectSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  {projectSiteUrl}
                </a>
              </div>
            )}
            {communityId && (
              <div className="flex items-center gap-1">
                <span className="text-gray-500">Community URL:</span>
                <a
                  href={`https://x.com/i/communities/${communityId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  https://x.com/i/communities/{communityId}
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 컨텐츠 영역 */}
      <div
        className="flex-1 overflow-hidden p-4"
        style={{ maxHeight: 'calc(100vh - 280px)' }}
      >
        {isEditing ? (
          /* 편집 모드 */
          <div className="markdown-editor-wrapper h-full flex flex-col" style={{ minHeight: '400px' }}>
              <SimpleMDE
                value={localEditedContents}
                onChange={setLocalEditedContents}
                options={editorOptions}
              />
          </div>
        ) : (
          /* 뷰 모드 */
          <div className="h-full overflow-y-auto markdown-content" style={{ maxHeight: 'calc(100vh - 320px)' }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: (props: any) => (
                  <h1 className="text-white text-3xl font-bold mb-6 mt-8 first:mt-0 border-b border-gray-700 pb-3" {...props}>
                    {props.children}
                  </h1>
                ),
                h2: (props: any) => (
                  <h2 className="text-white text-2xl font-semibold mb-4 mt-7 first:mt-0" {...props}>
                    {props.children}
                  </h2>
                ),
                h3: (props: any) => (
                  <h3 className="text-white text-xl font-semibold mb-3 mt-6 first:mt-0" {...props}>
                    {props.children}
                  </h3>
                ),
                h4: (props: any) => (
                  <h4 className="text-white text-lg font-semibold mb-2 mt-5 first:mt-0" {...props}>
                    {props.children}
                  </h4>
                ),
                p: (props: any) => <p className="text-gray-200 mb-5 leading-relaxed text-base" {...props} />,
                ul: (props: any) => <ul className="text-white list-disc list-outside  mb-5 ml-6 space-y-2" {...props} />,
                ol: (props: any) => <ol className="text-white list-decimal list-outside mb-5 ml-6 space-y-2" {...props} />,
                li: (props: any) => <li className="text-gray-200 leading-relaxed pl-1" {...props} />,
                code: (props: any) => {
                  const { inline, children, ...rest } = props;
                  return inline ? (
                    <code className="text-white bg-gray-700 px-2 py-0.5 rounded text-sm font-mono" {...rest}>
                      {children}
                    </code>
                  ) : (
                    <code className="text-white block bg-gray-800 p-4 rounded-lg text-sm overflow-x-auto mb-5 border border-gray-700 font-mono leading-relaxed" {...rest}>
                      {children}
                    </code>
                  );
                },
                pre: (props: any) => <pre className="text-white bg-gray-800 p-4 rounded-lg text-sm overflow-x-auto mb-5 border border-gray-700" {...props} />,
                blockquote: (props: any) => (
                  <blockquote className="border-l-4 border-blue-500 pl-6 pr-4 italic text-gray-300 mb-5 bg-gray-800/50 py-3 rounded-r" {...props} />
                ),
                a: (props: any) => (
                  <a
                    className="text-blue-400 hover:text-blue-300 underline transition-colors duration-200"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  >
                    {props.children}
                  </a>
                ),
                strong: (props: any) => <strong className="font-bold text-white" {...props} />,
                em: (props: any) => <em className="italic text-gray-200" {...props} />,
                hr: (props: any) => <hr className="border-gray-700 my-8" {...props} />,
                table: (props: any) => (
                  <div className="overflow-x-auto mb-5">
                    <table className="w-full border-collapse border border-gray-700" {...props} />
                  </div>
                ),
                thead: (props: any) => <thead className="bg-gray-800" {...props} />,
                tbody: (props: any) => <tbody {...props} />,
                th: (props: any) => (
                  <th className="border border-gray-700 bg-gray-800 px-4 py-3 text-left text-white font-semibold" {...props} />
                ),
                td: (props: any) => <td className="border border-gray-700 px-4 py-3 text-gray-200" {...props} />,
                img: (props: any) => (
                  <img className="rounded-lg mb-5 max-w-full h-auto" alt={props.alt || ''} {...props} />
                ),
              }}
            >
              {contents || "No contents"}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // contents, isEditing, 메타 정보가 변경되지 않으면 리렌더링 방지
  const prevFromTime = prevProps.contentsReferencedFrom instanceof Date 
    ? prevProps.contentsReferencedFrom.getTime() 
    : prevProps.contentsReferencedFrom;
  const nextFromTime = nextProps.contentsReferencedFrom instanceof Date 
    ? nextProps.contentsReferencedFrom.getTime() 
    : nextProps.contentsReferencedFrom;
  const prevToTime = prevProps.contentsReferencedTo instanceof Date 
    ? prevProps.contentsReferencedTo.getTime() 
    : prevProps.contentsReferencedTo;
  const nextToTime = nextProps.contentsReferencedTo instanceof Date 
    ? nextProps.contentsReferencedTo.getTime() 
    : nextProps.contentsReferencedTo;

  return (
    prevProps.contents === nextProps.contents &&
    prevProps.isEditing === nextProps.isEditing &&
    prevFromTime === nextFromTime &&
    prevToTime === nextToTime &&
    prevProps.projectSiteUrl === nextProps.projectSiteUrl
  );
});

ResearchReportEditor.displayName = 'ResearchReportEditor';

export default ResearchReportEditor;
